import { useState, useEffect, useMemo } from 'react';
import api, { API_ORIGIN } from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { todayStr } from '../utils/date';

const fmtN = (v) => { const n = String(v).replace(/[^0-9]/g,''); return n ? Number(n).toLocaleString() : ''; };
const pN = (v) => parseInt(String(v).replace(/[^0-9]/g,'')) || 0;

export default function SurveyJobsPage() {
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('직무·등급 등록/편집');
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // 'new' | id
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [acCat, setAcCat] = useState([]);
  const [acSurvey, setAcSurvey] = useState([]);
  const [acSkill, setAcSkill] = useState([]);
  const [showSim, setShowSim] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [csvRows, setCsvRows] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = async () => { try { setItems(await api.get('/survey-jobs')); } catch (e) { console.error(e); } };
  const loadAC = async () => {
    try {
      const [c, s, sk] = await Promise.all([
        api.get('/survey-jobs/autocomplete?field=job_category'),
        api.get('/survey-jobs/autocomplete?field=survey_job'),
        api.get('/survey-jobs/autocomplete?field=skill_level'),
      ]);
      setAcCat(c); setAcSurvey(s); setAcSkill(sk);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); loadAC(); }, []);

  // 직종으로 그룹화
  const grouped = useMemo(() => {
    const map = new Map();
    for (const sj of items) {
      if (!map.has(sj.job_category)) map.set(sj.job_category, []);
      map.get(sj.job_category).push(sj);
    }
    return Array.from(map.entries()).map(([cat, arr]) => ({ cat, arr }));
  }, [items]);

  const startNew = () => {
    setEditing('new');
    setForm({
      job_category:'', survey_job:'',
      sort_order: items.length,
      rates: [{ skill_level:'특급기술자', monthly_rate:'' }],
      itsqf_jobs: [{ itsqf_job:'', job_definition:'' }],
    });
  };
  const startEdit = (sj) => {
    setEditing(sj.id);
    setForm({
      job_category: sj.job_category || '',
      survey_job: sj.survey_job || '',
      sort_order: sj.sort_order || 0,
      rates: (sj.rates || []).map(r => ({ skill_level: r.skill_level, monthly_rate: String(r.monthly_rate || '') })),
      itsqf_jobs: (sj.itsqf_jobs || []).map(j => ({ id: j.id, itsqf_job: j.itsqf_job, job_definition: j.job_definition || '' })),
    });
  };
  const cancel = () => { setEditing(null); setForm(null); };

  // rates 조작
  const updRate = (i, k, v) => setForm(f => ({ ...f, rates: f.rates.map((r,j)=>j===i?{...r,[k]:v}:r) }));
  const addRate = () => setForm(f => ({ ...f, rates: [...f.rates, { skill_level:'', monthly_rate:'' }] }));
  const delRate = (i) => setForm(f => ({ ...f, rates: f.rates.filter((_,j)=>j!==i) }));
  const moveRate = (i, dir) => setForm(f => {
    const u = [...f.rates]; const j = i+dir; if (j<0||j>=u.length) return f;
    [u[i],u[j]] = [u[j],u[i]]; return { ...f, rates: u };
  });

  // itsqf_jobs 조작
  const updIt = (i, k, v) => setForm(f => ({ ...f, itsqf_jobs: f.itsqf_jobs.map((j,jj)=>jj===i?{...j,[k]:v}:j) }));
  const addIt = () => setForm(f => ({ ...f, itsqf_jobs: [...f.itsqf_jobs, { itsqf_job:'', job_definition:'' }] }));
  const delIt = (i) => setForm(f => ({ ...f, itsqf_jobs: f.itsqf_jobs.filter((_,j)=>j!==i) }));
  const moveIt = (i, dir) => setForm(f => {
    const u = [...f.itsqf_jobs]; const j = i+dir; if (j<0||j>=u.length) return f;
    [u[i],u[j]] = [u[j],u[i]]; return { ...f, itsqf_jobs: u };
  });

  const save = async () => {
    if (!form.job_category.trim() || !form.survey_job.trim()) {
      alert('직종 / 조사용직무는 필수입니다.'); return;
    }
    const rates = (form.rates||[]).filter(r => (r.skill_level||'').trim()).map(r => ({
      skill_level: r.skill_level.trim(),
      monthly_rate: pN(r.monthly_rate),
    }));
    const itsqf_jobs = (form.itsqf_jobs||[]).filter(j => (j.itsqf_job||'').trim()).map(j => ({
      id: j.id || undefined,
      itsqf_job: j.itsqf_job.trim(),
      job_definition: j.job_definition || null,
    }));
    setSaving(true);
    try {
      const body = {
        job_category: form.job_category.trim(),
        survey_job: form.survey_job.trim(),
        sort_order: parseInt(form.sort_order) || 0,
        rates,
        itsqf_jobs,
      };
      if (editing === 'new') await api.post('/survey-jobs', body);
      else await api.put(`/survey-jobs/${editing}`, body);
      setEditing(null); setForm(null);
      await load(); await loadAC();
    } catch (e) { alert(e.message); } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('이 조사용직무를 삭제하시겠습니까? 하위 ITSQF 직무와 등급 단가도 함께 사라집니다.')) return;
    try { await api.del(`/survey-jobs/${id}`); await load(); }
    catch (e) { alert(e.message); }
  };

  // CSV 내보내기
  const exportCsv = async () => {
    const token = localStorage.getItem('token');
    try {
      const r = await fetch(`${API_ORIGIN}/api/survey-jobs/export-csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('내보내기 실패');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `직무_등급_${todayStr()}.csv`;
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert(e.message); }
  };

  // CSV 파일 선택 → 파싱
  const handleCsvFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { alert('데이터가 없습니다.'); return; }
      const headers = lines[0].replace(/^﻿/, '').split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      const idx = {};
      headers.forEach((h, i) => { idx[h] = i; });

      const parseLine = (line) => {
        const out = []; let cur = '', q = false;
        for (let i = 0; i < line.length; i++) {
          const ch = line[i];
          if (ch === '"' && line[i+1] === '"') { cur += '"'; i++; }
          else if (ch === '"') { q = !q; }
          else if (ch === ',' && !q) { out.push(cur); cur = ''; }
          else { cur += ch; }
        }
        out.push(cur);
        return out;
      };

      const rows = lines.slice(1).map(line => {
        const v = parseLine(line);
        return {
          job_category: (v[idx['직종']] || '').trim(),
          survey_job:   (v[idx['조사용직무']] || '').trim(),
          rates_str:    v[idx['등급·월단가']] || '',
          itsqf:        (v[idx['ITSQF직무']] || '').trim(),
          def:          v[idx['직무정의']] || '',
        };
      }).filter(r => r.survey_job);

      // 매칭 표시용: 기존 조사용직무명 set
      const existingNames = new Set((items || []).map(x => x.survey_job));
      rows.forEach(r => { r._isExisting = existingNames.has(r.survey_job); });
      setCsvRows(rows);
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const submitImport = async () => {
    if (!csvRows || csvRows.length === 0) return;
    setImporting(true);
    try {
      const r = await api.post('/survey-jobs/import-csv', { rows: csvRows });
      const s = r.summary || {};
      const msg = `가져오기 완료: 신규 ${s.created||0}건, 업데이트 ${s.updated||0}건, 오류 ${s.errors||0}건`;
      alert(msg + ((r.errors||[]).length ? `\n\n${r.errors.slice(0,10).join('\n')}` : ''));
      setShowImport(false); setCsvRows(null);
      await load();
    } catch (e) { alert(e.message); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">직무·등급 관리</div>
          <div style={{display:'flex',gap:6}}>
            <button className="btn btn-outline btn-sm" onClick={exportCsv}>📥 CSV 내보내기</button>
            {canEdit && <button className="btn btn-outline btn-sm" onClick={()=>{ setShowImport(true); setCsvRows(null); }}>📤 CSV 가져오기</button>}
            {canEdit && <button className="btn btn-primary btn-sm" onClick={startNew} disabled={editing!==null}>+ 직무추가</button>}
          </div>
        </div>
        {editing !== null && form && (
          <div style={{background:'#fffbeb',border:'1px solid #fde68a',borderRadius:8,padding:16,marginBottom:16}}>
            <div style={{fontSize:14,fontWeight:700,marginBottom:10}}>{editing==='new'?'조사용직무 추가':'조사용직무 수정'}</div>
            <datalist id="ac-cat">{acCat.map(v => <option key={v} value={v} />)}</datalist>
            <datalist id="ac-survey">{acSurvey.map(v => <option key={v} value={v} />)}</datalist>
            <datalist id="ac-skill">{acSkill.map(v => <option key={v} value={v} />)}</datalist>

            <div className="grid-2" style={{marginBottom:14}}>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">직종 *</label>
                <input className="form-input" list="ac-cat" value={form.job_category} onChange={e=>setForm({...form,job_category:e.target.value})} placeholder="직종명을 입력" />
              </div>
              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label">조사용직무 *</label>
                <input className="form-input" list="ac-survey" value={form.survey_job} onChange={e=>setForm({...form,survey_job:e.target.value})} placeholder="조사용직무명을 입력" />
              </div>
            </div>

            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:6,marginBottom:6}}>
              <div style={{fontSize:13,fontWeight:700}}>📊 기술등급별 월단가</div>
              <button className="btn btn-outline btn-sm" onClick={()=>setShowSim(true)}>시뮬레이션</button>
            </div>
            <div style={{overflowX:'auto'}}><table style={{fontSize:12}}>
              <thead><tr><th style={{width:30}}></th><th>기술등급</th><th style={{width:160,textAlign:'right'}}>월단가</th><th style={{width:90}}></th></tr></thead>
              <tbody>
                {form.rates.map((r,i) => (
                  <tr key={i}>
                    <td style={{textAlign:'center',color:'#94a3b8'}}>{i+1}</td>
                    <td><input className="form-input" style={{padding:'4px 8px'}} list="ac-skill" value={r.skill_level} onChange={e=>updRate(i,'skill_level',e.target.value)} placeholder="등급명을 입력" /></td>
                    <td><input className="form-input" style={{padding:'4px 8px',textAlign:'right'}} value={fmtN(r.monthly_rate)} onChange={e=>updRate(i,'monthly_rate',e.target.value)} placeholder="월단가 입력" /></td>
                    <td style={{whiteSpace:'nowrap'}}>
                      <button className="btn btn-outline btn-sm" style={{padding:'2px 6px'}} onClick={()=>moveRate(i,-1)} disabled={i===0}>↑</button>
                      <button className="btn btn-outline btn-sm" style={{padding:'2px 6px',marginLeft:2}} onClick={()=>moveRate(i,1)} disabled={i===form.rates.length-1}>↓</button>
                      <button className="btn btn-danger btn-sm" style={{padding:'2px 6px',marginLeft:2}} onClick={()=>delRate(i)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
            <button className="btn btn-outline btn-sm" onClick={addRate} style={{marginTop:6}}>+ 등급 추가</button>

            <div style={{fontSize:13,fontWeight:700,marginTop:14,marginBottom:6}}>🧩 ITSQF 직무 ({form.itsqf_jobs.length}개)</div>
            {form.itsqf_jobs.map((j,i) => (
              <div key={i} style={{border:'1px solid #e2e8f0',borderRadius:6,padding:10,marginBottom:8,background:'#fff'}}>
                <div style={{display:'flex',gap:6,alignItems:'flex-start'}}>
                  <span style={{fontSize:11,color:'#94a3b8',marginTop:8,width:18,textAlign:'center'}}>{i+1}</span>
                  <div style={{flex:1}}>
                    <input className="form-input" style={{padding:'5px 8px',marginBottom:4,fontWeight:700}} value={j.itsqf_job} onChange={e=>updIt(i,'itsqf_job',e.target.value)} placeholder="ITSQF 직무명을 입력" />
                    <textarea className="form-input" rows={2} style={{padding:'5px 8px',fontSize:11}} value={j.job_definition} onChange={e=>updIt(i,'job_definition',e.target.value)} placeholder="직무 정의 (이 ITSQF 직무에 공통 적용)" />
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:2}}>
                    <button className="btn btn-outline btn-sm" style={{padding:'2px 6px'}} onClick={()=>moveIt(i,-1)} disabled={i===0}>↑</button>
                    <button className="btn btn-outline btn-sm" style={{padding:'2px 6px'}} onClick={()=>moveIt(i,1)} disabled={i===form.itsqf_jobs.length-1}>↓</button>
                    <button className="btn btn-danger btn-sm" style={{padding:'2px 6px'}} onClick={()=>delIt(i)}>✕</button>
                  </div>
                </div>
              </div>
            ))}
            <button className="btn btn-outline btn-sm" onClick={addIt}>+ ITSQF 직무 추가</button>

            <div style={{display:'flex',gap:8,marginTop:16}}>
              <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'저장 중...':'저장'}</button>
              <button className="btn btn-outline" onClick={cancel}>취소</button>
            </div>
          </div>
        )}

        {grouped.length === 0 && editing === null && (
          <div style={{textAlign:'center',padding:32,color:'#94a3b8'}}>등록된 조사용직무가 없습니다.</div>
        )}

        {grouped.map(g => (
          <div key={g.cat} style={{marginBottom:18}}>
            <div style={{fontSize:13,fontWeight:700,color:'#3b82f6',marginBottom:8,paddingBottom:4,borderBottom:'2px solid #dbeafe'}}>{g.cat}</div>
            {g.arr.map(sj => (
              <div key={sj.id} style={{border:'1px solid #e2e8f0',borderRadius:8,padding:12,marginBottom:10,background:'#fff'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8}}>
                  <div style={{fontSize:15,fontWeight:700}}>{sj.survey_job}</div>
                  <div>
                    {canEdit && <>
                      <button className="btn btn-outline btn-sm" onClick={()=>startEdit(sj)} disabled={editing!==null} style={{marginRight:4}}>수정</button>
                      <button className="btn btn-danger btn-sm" onClick={()=>remove(sj.id)} disabled={editing!==null}>삭제</button>
                    </>}
                  </div>
                </div>

                <div style={{display:'flex',gap:14,flexWrap:'wrap'}}>
                  <div style={{flex:'1 1 280px',minWidth:260}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',marginBottom:4}}>📊 기술등급별 월단가</div>
                    {(sj.rates||[]).length > 0 ? (
                      <div style={{overflowX:'auto'}}><table style={{fontSize:12}}>
                        <tbody>
                          {sj.rates.map(r => (
                            <tr key={r.id}><td style={{padding:'3px 8px'}}>{r.skill_level}</td><td style={{padding:'3px 8px',textAlign:'right',fontWeight:600}}>₩{Number(r.monthly_rate||0).toLocaleString()}</td></tr>
                          ))}
                        </tbody>
                      </table></div>
                    ) : null}
                  </div>
                  <div style={{flex:'2 1 380px',minWidth:300}}>
                    <div style={{fontSize:11,fontWeight:700,color:'#94a3b8',marginBottom:4}}>🧩 ITSQF 직무 ({(sj.itsqf_jobs||[]).length}개)</div>
                    {(sj.itsqf_jobs||[]).length > 0 ? (
                      <div style={{display:'flex',flexDirection:'column',gap:4}}>
                        {sj.itsqf_jobs.map(j => (
                          <div key={j.id} style={{background:'#f8fafc',padding:'6px 10px',borderRadius:6,fontSize:12}}>
                            <div style={{fontWeight:700}}>{j.itsqf_job}</div>
                            {j.job_definition && <div style={{color:'#475569',whiteSpace:'pre-wrap',fontSize:11,marginTop:2}}>{j.job_definition}</div>}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {showSim && (
        <RateSimulatorModal
          onClose={()=>setShowSim(false)}
          onSave={(rates)=>{
            if (form && Array.isArray(form.rates) && form.rates.length > 0) {
              if (!window.confirm('기존 입력한 기술등급별 월단가가 모두 덮어쓰여집니다. 진행할까요?')) return;
            }
            setForm(f => ({ ...f, rates }));
            setShowSim(false);
          }}
        />
      )}

      {showImport && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowImport(false)}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:820,maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>CSV 가져오기</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowImport(false)}>✕</span>
            </div>

            <div style={{background:'#f8fafc',border:'1px solid #e2e8f0',borderRadius:8,padding:'10px 12px',marginBottom:12,fontSize:12,lineHeight:1.7,color:'#475569'}}>
              <div style={{fontWeight:700,marginBottom:4}}>형식 안내</div>
              컬럼: <code>직종</code>, <code>조사용직무</code>, <code>등급·월단가</code>, <code>ITSQF직무</code>, <code>직무정의</code><br/>
              · <strong>1행 = 1 ITSQF 직무</strong>. 같은 조사용직무가 N개 ITSQF 를 가지면 N 행 (등급·월단가는 반복)<br/>
              · <strong>등급·월단가</strong>: <code>초급기술자;10000000;중급기술자;13000000;…</code> (이름;값;이름;값 교대)<br/>
              · 조사용직무명으로 그룹화·매칭 — 기존이면 덮어쓰기(Upsert), 없으면 신규 생성. CSV에 없는 기존 항목은 유지됨.
            </div>

            <div style={{marginBottom:12}}>
              <input type="file" accept=".csv" onChange={handleCsvFile} />
            </div>

            {csvRows && csvRows.length > 0 && (
              <>
                {(() => {
                  const uniqJobs = new Set(csvRows.map(r => r.survey_job));
                  const existingNames = new Set((items || []).map(x => x.survey_job));
                  const newCount = [...uniqJobs].filter(n => !existingNames.has(n)).length;
                  const updCount = uniqJobs.size - newCount;
                  return (
                    <div style={{fontSize:12,marginBottom:6,color:'#64748b'}}>
                      총 {csvRows.length}행 · 조사용직무 {uniqJobs.size}개 · 신규 {newCount}개 · 업데이트 {updCount}개
                    </div>
                  );
                })()}
                <div style={{maxHeight:340,overflowY:'auto',overflowX:'auto',border:'1px solid #e2e8f0',borderRadius:8}}>
                  <table style={{fontSize:11,width:'100%'}}>
                    <thead style={{position:'sticky',top:0,background:'#f8fafc'}}>
                      <tr>
                        <th style={{width:30,padding:'6px 8px'}}>#</th>
                        <th style={{width:60,padding:'6px 8px'}}>상태</th>
                        <th style={{width:80,padding:'6px 8px'}}>직종</th>
                        <th style={{padding:'6px 8px'}}>조사용직무</th>
                        <th style={{padding:'6px 8px'}}>ITSQF직무</th>
                        <th style={{padding:'6px 8px'}}>등급·월단가</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvRows.map((r,i) => (
                        <tr key={i} style={{borderTop:'1px solid #f1f5f9'}}>
                          <td style={{padding:'4px 8px',color:'#94a3b8'}}>{i+1}</td>
                          <td style={{padding:'4px 8px'}}>
                            <span className={`badge ${r._isExisting?'badge-yellow':'badge-blue'}`}>{r._isExisting?'업데이트':'신규'}</span>
                          </td>
                          <td style={{padding:'4px 8px'}}>{r.job_category||'-'}</td>
                          <td style={{padding:'4px 8px',fontWeight:600}}>{r.survey_job}</td>
                          <td style={{padding:'4px 8px',color:'#475569'}}>{r.itsqf||'-'}</td>
                          <td style={{padding:'4px 8px',color:'#475569',fontSize:10}}>{r.rates_str||'-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              <button className="btn btn-outline" onClick={()=>setShowImport(false)}>취소</button>
              <button className="btn btn-primary" disabled={!csvRows||csvRows.length===0||importing} onClick={submitImport}>
                {importing?'가져오는 중...':`가져오기 ${csvRows?`(${csvRows.length}건)`:''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 시뮬레이터 모달 — P25 / P75 / 평균 입력 → 초/중/고/특 4단계 단가 자동 산정 (0.92 보정)
// 결과 행은 편집 가능하며 그래프에 실시간 반영됨
const SIM_FACTOR = 0.92;
const MM_DAYS = 20.6;
const SKILL_LEVELS = ['초급기술자', '중급기술자', '고급기술자', '특급기술자'];

function RateSimulatorModal({ onClose, onSave }) {
  const [p25, setP25] = useState('');
  const [p75, setP75] = useState('');
  const [m, setM] = useState('');
  const [overhead, setOverhead] = useState(''); // 제경비 % (0~120)
  const [tech, setTech] = useState('');         // 기술료 % (0~40)
  const [rows, setRows] = useState(SKILL_LEVELS.map(sl => ({ skill_level: sl, md: 0, monthly: 0 })));

  const overheadPct = Math.min(120, Math.max(0, parseFloat(overhead) || 0));
  const techPct     = Math.min(40,  Math.max(0, parseFloat(tech) || 0));
  const finalMultiplier = 1 + (overheadPct / 100) + (techPct / 100);
  const finalOf = (monthly) => Math.round(monthly * finalMultiplier);

  const v25 = pN(p25);
  const v75 = pN(p75);
  const vM  = pN(m);
  const allFilled = v25 > 0 && v75 > 0 && vM > 0;
  const valid = !allFilled || (v25 <= vM && vM <= v75);

  // P25/P75/M 변경 시 — 자동 산정 (보정계수 0.92 적용)
  useEffect(() => {
    if (!allFilled || !valid) return;
    const md = [
      (0.7 * v25 + 0.3 * vM) * SIM_FACTOR,
      (0.4 * vM  + 0.6 * v75) * SIM_FACTOR,
      (v75 + (vM - v25) * 0.2) * SIM_FACTOR,
      (v75 + (v75 - vM) * 1.5) * SIM_FACTOR,
    ];
    setRows(SKILL_LEVELS.map((sl, i) => ({
      skill_level: sl,
      md: Math.round(md[i]),
      monthly: Math.round(md[i] * MM_DAYS),
    })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v25, v75, vM, allFilled, valid]);

  // M/D 편집 시 — 해당 행의 monthly 도 동기화
  const editMd = (i, val) => {
    const md = pN(val);
    setRows(rows.map((r, j) => j === i ? { ...r, md, monthly: Math.round(md * MM_DAYS) } : r));
  };
  // monthly 편집 시 — 해당 행의 md 도 역산 (표시용)
  const editMonthly = (i, val) => {
    const monthly = pN(val);
    setRows(rows.map((r, j) => j === i ? { ...r, monthly, md: Math.round(monthly / MM_DAYS) } : r));
  };

  const refMonthly = vM > 0 ? Math.round(vM * MM_DAYS) : 0;
  const hasResults = rows.some(r => r.monthly > 0);
  const handleSave = () => {
    if (!hasResults) return;
    // 최종단가(M/M) = M/M 단가 × (1 + 제경비% + 기술료%) 를 monthly_rate 로 저장
    onSave(rows.map(r => ({ skill_level: r.skill_level, monthly_rate: String(finalOf(r.monthly)) })));
  };

  // 막대그래프 SVG — 최종단가(M/M) 기준 막대 / 강조선은 평균임금(제경비·기술료 미반영)
  // 5분할 슬롯에 4 막대 배치 (좌측 첫 슬롯은 강조선 라벨 공간으로 비움)
  const H = 200, W = 480, PAD_L = 24, PAD_R = 24, PAD_T = 30, PAD_B = 36;
  const innerH = H - PAD_T - PAD_B;
  const innerW = W - PAD_L - PAD_R;
  const gap = innerW / 5;
  const barW = gap * 0.5;
  const finalValues = rows.map(r => finalOf(r.monthly));
  const maxVal = Math.max(...finalValues, refMonthly, 1) * 1.12;
  const yOf = (v) => PAD_T + innerH - (v / maxVal) * innerH;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:600,maxWidth:'94vw',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
          <span>월단가 시뮬레이터</span>
          <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={onClose}>✕</span>
        </div>

        <div style={{fontSize:11,color:'#94a3b8',marginBottom:10}}>
          M/D 단가를 입력하면 4단계 등급의 단가를 자동 산정합니다 (월 = M/D × {MM_DAYS}, 보정계수 {SIM_FACTOR} 적용). 결과는 수기로 수정 가능하며 그래프에 실시간 반영됩니다.
        </div>

        <div className="form-group">
          <label className="form-label">25백분위수(M/D)</label>
          <input className="form-input" style={{textAlign:'right'}} value={fmtN(p25)} onChange={e=>setP25(e.target.value)} placeholder="원" />
        </div>
        <div className="form-group">
          <label className="form-label">75백분위수(M/D)</label>
          <input className="form-input" style={{textAlign:'right'}} value={fmtN(p75)} onChange={e=>setP75(e.target.value)} placeholder="원" />
        </div>
        <div className="form-group">
          <label className="form-label">평균임금(M/D)</label>
          <input className="form-input" style={{textAlign:'right'}} value={fmtN(m)} onChange={e=>setM(e.target.value)} placeholder="원" />
        </div>

        <div className="grid-2">
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label">제경비 (%)</label>
            <input className="form-input" type="number" min="0" max="120" step="0.1" style={{textAlign:'right'}} value={overhead} onChange={e=>setOverhead(e.target.value)} placeholder="0 ~ 120" />
          </div>
          <div className="form-group" style={{marginBottom:0}}>
            <label className="form-label">기술료 (%)</label>
            <input className="form-input" type="number" min="0" max="40" step="0.1" style={{textAlign:'right'}} value={tech} onChange={e=>setTech(e.target.value)} placeholder="0 ~ 40" />
          </div>
        </div>
        <div style={{fontSize:11,color:'#94a3b8',marginTop:4,marginBottom:12}}>
          최종단가(M/M) = M/M단가 × (1 + 제경비 + 기술료) = M/M × {finalMultiplier.toFixed(3)}
        </div>

        {!valid && (
          <div style={{background:'#fef2f2',border:'1px solid #fecaca',color:'#dc2626',padding:'8px 12px',borderRadius:6,fontSize:12,marginBottom:12}}>
            ⚠ P25 ≤ 평균(M) ≤ P75 관계를 만족해야 합니다.
          </div>
        )}

        <div style={{fontSize:13,fontWeight:700,marginTop:8,marginBottom:6}}>📊 산정 결과 <span style={{fontSize:11,fontWeight:400,color:'#94a3b8'}}>(M/D · M/M 수기 조정 가능 · 최종단가는 자동 계산)</span></div>
        <table style={{fontSize:12}}>
          <thead>
            <tr>
              <th style={{width:90}}>등급</th>
              <th style={{textAlign:'right'}}>M/D 단가</th>
              <th style={{textAlign:'right'}}>M/M 단가</th>
              <th style={{textAlign:'right'}}>최종단가(M/M)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const finalVal = finalOf(row.monthly);
              return (
                <tr key={row.skill_level}>
                  <td className="fw-bold">{row.skill_level}</td>
                  <td><input className="form-input" style={{padding:'3px 8px',textAlign:'right',fontSize:11}} value={fmtN(row.md)} onChange={e=>editMd(i, e.target.value)} /></td>
                  <td><input className="form-input" style={{padding:'3px 8px',textAlign:'right',fontSize:11}} value={fmtN(row.monthly)} onChange={e=>editMonthly(i, e.target.value)} /></td>
                  <td style={{textAlign:'right',fontWeight:700,color:'#3b82f6'}}>{finalVal>0?`₩${finalVal.toLocaleString()}`:'-'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{fontSize:13,fontWeight:700,marginTop:14,marginBottom:6}}>📈 최종단가(M/M) 그래프</div>
        <div style={{background:'#f8fafc',borderRadius:8,padding:'6px 4px'}}>
          <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
            {/* 기준선 (0) */}
            <line x1={PAD_L} y1={H-PAD_B} x2={W-PAD_R} y2={H-PAD_B} stroke="#cbd5e1" strokeWidth="1" />
            {/* 가로 강조선 (평균임금 × 20.6 — 제경비·기술료 미반영) */}
            {refMonthly > 0 && (
              <g>
                <line x1={PAD_L} y1={yOf(refMonthly)} x2={W-PAD_R} y2={yOf(refMonthly)} stroke="#ef4444" strokeDasharray="5 4" strokeWidth="1.5" />
                <text x={PAD_L} y={yOf(refMonthly)-18} textAnchor="start" fontSize="11" fontWeight="700" fill="#ef4444">
                  <tspan x={PAD_L} dy="0">평균임금</tspan>
                  <tspan x={PAD_L} dy="1.2em">₩{refMonthly.toLocaleString()}원</tspan>
                </text>
              </g>
            )}
            {/* 막대 (최종단가) — 좌측 첫 슬롯은 비워서 강조선 라벨 공간 확보 */}
            {rows.map((r, i) => {
              const v = finalValues[i];
              const cx = PAD_L + gap*(i+1+0.5);
              const y = v > 0 ? yOf(v) : H-PAD_B;
              const h = v > 0 ? (H-PAD_B) - y : 0;
              return (
                <g key={i}>
                  <rect x={cx-barW/2} y={y} width={barW} height={h} fill="#3b82f6" rx="2" />
                  {v > 0 && <text x={cx} y={y-5} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b">₩{v.toLocaleString()}</text>}
                  <text x={cx} y={H-PAD_B+16} textAnchor="middle" fontSize="11" fill="#475569">{r.skill_level}</text>
                </g>
              );
            })}
          </svg>
        </div>

        <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
          <button className="btn btn-primary" disabled={!hasResults} onClick={handleSave}>저장</button>
          <button className="btn btn-outline" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}
